/**
 * Form context keys the server owns (rms_forms._SERVER_OWNED_CONTEXT_KEYS).
 * The server reads them from the patient record on every bundle, save and
 * submit, and returns them with the form bundle:
 *   - initiatedBy: who started the referral ('school' | 'guardian')
 *   - subjectSex: the child's sex at birth as recorded ('Male', 'Female', ...)
 *   - schoolPhase: 'primary' (Reception to Year 6) | 'secondary'
 */
export const SERVER_OWNED_CONTEXT_KEYS = ['initiatedBy', 'subjectSex', 'schoolPhase'];

const isEmpty = (value) => value === undefined || value === null || value === '';

// subject.sex is stored as a label ('Male'); a bare option id is mapped the
// same way the server maps it (rms_forms._CYP_DEMOGRAPHIC_LABELS).
const SEX_LABELS = {
  male: 'Male',
  female: 'Female',
  intersex: 'Intersex',
  non_binary: 'Non-binary',
  not_sure: 'Not sure',
  prefer_not_say: 'Prefer not to say',
  prefer_not_to_say: 'Prefer not to say',
  other: 'Other',
};

/**
 * 'primary' for Reception to Year 6, 'secondary' for Year 7 to Year 13,
 * otherwise null (rms_harbour._school_phase_from_year_group).
 */
export function schoolPhaseFromYearGroup(yearGroup) {
  if (yearGroup === 'Reception') return 'primary';
  const n = Number.parseInt(String(yearGroup || '').replace('Year ', '').trim(), 10);
  if (!Number.isFinite(n)) return null;
  if (n >= 1 && n <= 6) return 'primary';
  if (n >= 7 && n <= 13) return 'secondary';
  return null;
}

/**
 * The child's sex and school phase as the server derives them from the
 * patient record (rms_forms._record_form_context), for a caller who can read
 * the record (a parent or a SENCO), so the screen agrees with the server.
 * Unknown values are left out: an unknown never hides a question.
 *
 * @param {object|null} record - patientRecords doc data
 * @returns {{ subjectSex?: string, schoolPhase?: string }}
 */
export function recordFormContext(record) {
  const data = record || {};
  const out = {};
  const sex = data.subject && data.subject.sex;
  if (typeof sex === 'string' && sex.trim()) {
    out.subjectSex = SEX_LABELS[sex.trim().toLowerCase()] || sex.trim();
  }
  const stored = data.schoolPhase || (data.school && data.school.schoolType);
  const phase = ['primary', 'secondary'].includes(stored) ? stored : schoolPhaseFromYearGroup(data.yearGroup);
  if (phase) out.schoolPhase = phase;
  return out;
}

/**
 * The page's form context with any server-owned key it lacks filled from the
 * context the server returned. A caller who cannot read the record (a class
 * teacher, TDI-489) has none of these keys of its own; without the server's
 * values every section gated on them disappears (TDI-678). Keys the caller
 * already holds are kept, so a parent's screen still follows the record they
 * read. Returns the same object when nothing needs filling.
 *
 * @param {object} formContext - the context built by useRmsFlowContext
 * @param {object|null} serverContext - the context from rmsGetPostFormBundle
 * @returns {object}
 */
export function withServerFormContext(formContext, serverContext) {
  const base = formContext || {};
  if (!serverContext) return base;
  const fills = {};
  SERVER_OWNED_CONTEXT_KEYS.forEach((key) => {
    if (isEmpty(base[key]) && !isEmpty(serverContext[key])) fills[key] = serverContext[key];
  });
  return Object.keys(fills).length ? { ...base, ...fills } : base;
}
